import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["open", "measuredHeight", "transitioning"];
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { createRef, forwardRef, PureComponent } from 'react';
import devLogger from 'react-utils/devLogger';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { TwoWayTransition } from '../TwoWayTransition';
import { memoizedSequence } from '../../utils/functions';
import { ACCORDION_TRANSITION_TIMING } from '../../../constants/times';
import styled from 'foundations-theming/utils';
import { PlainWrapper } from '../TwoWayTransition/TwoWayTransition';
import { jsx as _jsx } from "react/jsx-runtime";
const transitionTime = parseInt(ACCORDION_TRANSITION_TIMING, 10);
export const AccordionTransitionWrapper = styled( /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      open,
      transitioning
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(PlainWrapper, Object.assign({
    "data-open-complete": open && !transitioning
  }, rest, {
    ref: ref
  }));
}))(({
  duration,
  measuredHeight,
  nextOpen,
  open,
  transitioning: transitioningProp
}) => {
  const transitioning = duration > 0 && (transitioningProp || open !== nextOpen);
  const transition = measuredHeight != null && transitioning ? `height ${duration}ms cubic-bezier(0.25, 0.1, 0.25, 1),
         opacity ${duration}ms cubic-bezier(0.42, 0, 0.58, 1),
         transform ${duration}ms cubic-bezier(0.42, 0, 0.58, 1)` : 'none';
  const transform = transitioning ? `translateY(${open ? '0' : '-10px'})` : undefined;
  return {
    transform,
    transition,
    height: transitioning ? open ? `${measuredHeight}px` : 0 : undefined,
    opacity: transitioning ? open ? 1 : 0 : undefined,
    pointerEvents: transitioning ? 'none' : undefined,
    overflow: !open || transitioning ? 'hidden' : 'visible'
  };
});
export class AccordionTransition extends PureComponent {
  static getDerivedStateFromProps(props, state) {
    let derivedState = null;
    if (state.prevOpen !== props.open) {
      derivedState = {
        prevOpen: props.open
      };
      if (!state.transitioning) {
        derivedState = Object.assign({}, derivedState, {
          measuredHeight: null
        });
      }
    }
    return derivedState;
  }
  constructor(props) {
    super(props);
    this.wrapperRef = /*#__PURE__*/createRef();
    this.handleTransitionStart = () => {
      this.setState({
        transitioning: true
      });
    };
    this.handleTransitionComplete = () => {
      this.setState({
        transitioning: false
      });
    };
    this.state = {
      measuredHeight: 0,
      // 0 = not needed, null = needed
      transitioning: false,
      prevOpen: props.open
    };
  }
  componentDidUpdate() {
    if (this.state.measuredHeight == null) {
      this.measureHeight();
    }
  }
  measureHeight() {
    const el = this.wrapperRef.current;
    if (!el) {
      if (process.env.NODE_ENV !== 'production') {
        devLogger.warn({
          message: 'AccordionTransition: the `Wrapper` did not forward a ref to a host element. The open/close height animation will not run. Wrap the component in `forwardRef` and pass the ref to a DOM element.',
          key: 'AccordionTransition: Wrapper missing ref forwarding'
        });
      }
      return;
    }
    this.setState({
      measuredHeight: el.scrollHeight
    });
  }
  render() {
    const _this$props = this.props,
      {
        children,
        Content,
        renderContent,
        duration = transitionTime,
        onOpenStart,
        onCloseStart,
        onOpenComplete,
        onCloseComplete,
        open,
        Wrapper = AccordionTransitionWrapper,
        [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, ["children", "Content", "renderContent", "duration", "onOpenStart", "onCloseStart", "onOpenComplete", "onCloseComplete", "open", "Wrapper", DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE].map(_toPropertyKey));
    let renderedContent = null;
    if (open) {
      if (Content) {
        renderedContent = /*#__PURE__*/_jsx(Content, {});
      } else if (renderContent) {
        renderedContent = renderContent();
      } else {
        renderedContent = children;
      }
    }
    return /*#__PURE__*/_jsx(TwoWayTransition, Object.assign({}, rest, {
      Wrapper: Wrapper,
      wrapperRef: this.wrapperRef,
      duration: duration,
      measuredHeight: this.state.measuredHeight,
      onOpenStart: memoizedSequence(this.handleTransitionStart, onOpenStart),
      onCloseStart: memoizedSequence(this.handleTransitionStart, onCloseStart),
      onOpenComplete: memoizedSequence(this.handleTransitionComplete, onOpenComplete),
      onCloseComplete: memoizedSequence(this.handleTransitionComplete, onCloseComplete),
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute || 'AccordionTransition',
      children: renderedContent
    }));
  }
}
AccordionTransition.displayName = 'AccordionTransition';